<template>
  <div class="mask" @click.self="onClickOutside">
    <form ref="dialogForm" class="dialog" role="dialog" aria-modal="true" :aria-labelledby="titleId" tabindex="-1"
      @submit.prevent="onClickNext">
      <header class="dialog__header">
        <h2 :id="titleId" class="dialog__title">{{ $t("proofreadingItemSelectDialog.title") }}</h2>
        <button type="button" class="dialog__close" :aria-label="$t('proofreadingItemSelectDialog.close')"
          :disabled="isExecuting" @click="onClickClose">
          ×
        </button>
      </header>

      <div class="dialog__body" :class="{ 'dialog__body--disabled': isExecuting }">
        <div class="proper-noun-exclude">
          <div class="proper-noun-exclude__row">
            <button
              type="button"
              class="proper-noun-exclude__toggle"
              role="switch"
              :aria-checked="excludeProperNounsEnabled"
              :disabled="isExecuting"
              @click="onToggleExcludeProperNouns"
            >
              <span
                class="switch"
                :data-on="excludeProperNounsEnabled"
              >
                <span class="switch__knob" />
              </span>
              <span class="proper-noun-exclude__label">
                {{ $t("proofreadingItemSelectDialog.excludeProperNouns") }}
              </span>
            </button>
            <button
              type="button"
              class="proper-noun-exclude__settings"
              :aria-label="$t('proofreadingItemSelectDialog.excludeProperNounsSettings')"
              :disabled="isExecuting || !excludeProperNounsEnabled"
              @click="onClickProperNounSettings"
            >
              <IconSlider />
            </button>
          </div>
        </div>

        <div class="list">
          <proofreading-category-accordion v-for="(category) in groupedCategories" :key="category.id">
            <template #header-leading>
              <span class="category-checkbox" :data-disabled="isPremiumCategory(category)"
                @click="onToggleCategory(category)">
                <CheckboxIcon :is-checked="isCategoryChecked(category)"
                  :fill="checkboxFill(isCategoryChecked(category))" />
              </span>
            </template>
            <template #header>
              <span class="category-header-label-row">
                <span class="category-header-label">{{ category.label }}</span>
                <PremiumBadgeLink v-if="isPremiumCategory(category)" tooltip-placement="above" />
              </span>
            </template>

            <li v-for="(option, optionIndex) in category.options" :key="option.id" class="row row--option"
              :class="optionRowClass(optionIndex)" @click="onToggleOption(category, option)">
              <CheckboxIcon :is-checked="isOptionChecked(option.id)" :fill="checkboxFill(isOptionChecked(option.id))" />
              <span class="label">{{ option.label }}</span>
              <button type="button" class="info-button"
                :aria-label="$t('proofreadingItemSelectDialog.showRuleDescription', { label: option.label })"
                @click.stop="onClickRuleInfo(option)">
                <IconInfo />
              </button>
            </li>
          </proofreading-category-accordion>
        </div>
      </div>

      <footer class="dialog__footer">
        <p v-if="proceedBlockReason" class="dialog__blocker" role="status">
          {{ proceedBlockReason }}
        </p>
        <button
          v-if="fishEstimateError"
          type="button"
          class="retry-button"
          :disabled="isExecuting || isFishEstimateLoading"
          @click="loadFishEstimate"
        >
          {{ $t("proofreadingItemSelectDialog.retry") }}
        </button>
        <button type="submit" class="next-button" :disabled="!canProceed || isExecuting">
          {{
            isExecuting
              ? $t("proofreadingReviseManuscriptPage.loading")
              : $t("proofreadingItemSelectDialog.runProofreading")
          }}
        </button>
      </footer>
    </form>

    <proper-noun-modal
      v-if="isProperNounModalOpen"
      class="proper-noun-modal--over-proofreading"
      :novelId="novelId"
      @close="onProperNounModalClose"
    />
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import CheckboxIcon from "@/components/atoms/CheckboxIcon.vue";
import ProofreadingCategoryAccordion from "@/components/organisms/proofreading/shared/ProofreadingCategoryAccordion.vue";
import ProperNounModal from "@/components/organisms/ProperNounModal.vue";
import PremiumBadgeLink from "@/components/ui/exportManuscriptDialogs/PremiumBadgeLink.vue";
import ProofreadingRuleDescriptionDialog from "@/components/ui/dialogs/ProofreadingRuleDescriptionDialog.vue";
import ProofreadingFishConfirmDialog, {
  ProofreadingFishConfirmDialogOptions,
  ProofreadingFishConfirmDialogResult,
} from "@/components/ui/dialogs/ProofreadingFishConfirmDialog.vue";
import ProofreadingProperNounExcludeDialog, {
  ProofreadingProperNounExcludeDialogOptions,
  ProofreadingProperNounExcludeDialogResult,
} from "@/components/ui/dialogs/ProofreadingProperNounExcludeDialog.vue";
import {
  estimateProofreadingFish,
  EstimateProofreadingFishResponse,
  PostRuleBasedManuscriptParam,
  ProofreadingCategoryEntry,
  ProofreadingRuleEntry,
} from "@/lib/api/proofreading";
import type { ProperNoun } from "@/lib/models/properNoun";
import { Plan } from "@/lib/models/user";
import { Dialog } from "@/lib/utils/dialog";
import { calcExpectedProofreadingCharge } from "@/lib/utils/proofreadingFish";
import { PROOFREADING_FISH_MAX_CHARS } from "@/lib/utils/proofreadingFishTiers";
import { isFreeProofreadingCategory } from "@/lib/utils/proofreadingRuleCategories";
import { useProofreadingBasicStore } from "@/stores/proofreadingBasic";
import IconInfo from "~icons/figma/Info";
import IconSlider from "~icons/figma/Slider";

/**
 * 表示用に「カテゴリ + そこに属するルール」をネスト化したビューモデル。
 *
 * API レスポンス（`rules[]` フラット）を `category` でグルーピングし、
 * `categories[]` の順序で並べ替える。
 */
type GroupedRuleOption = {
  id: string;
  label: string;
  description: string;
};

type GroupedCategoryView = {
  id: string;
  label: string;
  options: GroupedRuleOption[];
};

/**
 * 校正項目選択モーダル。
 *
 * 元 `ProofreadingBasic.vue`（独立ページ）をモーダル化したもの。
 * 校正ルールは `useProofreadingBasicStore` にロード済みのものを参照する。
 * 選択結果（`selectedRuleIds`）は同ストアに保存して次フロー（修正反映画面）へ持ち越す。
 *
 * Vue Modal Dialogs (`vue-modal-dialogs`) で `this.$close(value)` を呼ぶ前提:
 *  - 「校正を実行」: `this.$close({ confirmed: true, selectedRuleIds })`
 *  - `onConfirm` 指定時: コールバック成功後に上記と同様にクローズ
 *  - 「×」/ 外クリック / ESC: `this.$close({ confirmed: false, selectedRuleIds: [] })`
 */
export default Vue.extend({
  components: {
    CheckboxIcon,
    IconInfo,
    IconSlider,
    PremiumBadgeLink,
    ProofreadingCategoryAccordion,
    ProperNounModal,
  },
  props: {
    onConfirm: {
      type: Function as PropType<(selectedRuleIds: string[]) => Promise<void>>,
      default: undefined,
    },
    novelId: {
      type: String,
      default: "",
    },
    manuscripts: {
      type: Array as PropType<PostRuleBasedManuscriptParam[]>,
      default: () => [],
    },
    /** 直近の支払いセッション累計（不明なら 0 扱い＝全額見積り） */
    sessionUsedFish: {
      type: Number as PropType<number | null>,
      default: null,
    },
  },
  data() {
    return {
      titleId: "proofreading-item-select-dialog-title",
      proofreadingBasicStore: useProofreadingBasicStore(),
      checkedFill: "var(--color-primary)",
      uncheckedFill: "var(--color-inactive)",
      isExecuting: false,
      isDescriptionDialogOpen: false,
      isFishEstimateLoading: false,
      fishEstimateError: false,
      fishEstimate: null as EstimateProofreadingFishResponse | null,
      isProperNounModalOpen: false,
      isProperNounExcludeDialogOpen: false,
      isProperNounLoading: false,
      isFishConfirmDialogOpen: false,
    };
  },
  computed: {
    rules(): ProofreadingRuleEntry[] {
      return this.proofreadingBasicStore.rules;
    },
    categories(): ProofreadingCategoryEntry[] {
      return this.proofreadingBasicStore.categories;
    },
    selectedRuleIds(): string[] {
      return this.proofreadingBasicStore.selectedRuleIds;
    },
    selectedRuleIdSet(): Set<string> {
      return this.proofreadingBasicStore.selectedRuleIdSet;
    },
    excludeProperNounsEnabled(): boolean {
      return this.proofreadingBasicStore.excludeProperNounsEnabled;
    },
    excludedProperNounIds(): string[] | null {
      return this.proofreadingBasicStore.excludedProperNounIds;
    },
    properNouns(): ProperNoun[] {
      return (this.$store.getters["properNounModule/properNounList"] as ProperNoun[]) ?? [];
    },
    groupedCategories(): GroupedCategoryView[] {
      const rulesByCategory = new Map<string, ProofreadingRuleEntry[]>();
      this.rules.forEach((rule: ProofreadingRuleEntry) => {
        const list = rulesByCategory.get(rule.category) ?? [];
        list.push(rule);
        rulesByCategory.set(rule.category, list);
      });

      return this.categories
        .map((category: ProofreadingCategoryEntry): GroupedCategoryView | null => {
          const entries = rulesByCategory.get(category.category) ?? [];
          if (entries.length === 0) {
            return null;
          }
          return {
            id: category.category,
            label: category.label,
            options: entries.map((rule: ProofreadingRuleEntry) => ({
              id: rule.rule,
              label: rule.label,
              description: rule.description,
            })),
          };
        })
        .filter((c): c is GroupedCategoryView => c !== null);
    },
    expectedCharge(): ReturnType<typeof calcExpectedProofreadingCharge> {
      return calcExpectedProofreadingCharge({
        estimatedFish: this.fishEstimate?.estimatedFish,
        sessionUsedFish: this.effectiveSessionUsedFish,
      });
    },
    /** 見積り API の sessionUsedFish を優先。未返却時はメモリ上の値にフォールバック */
    effectiveSessionUsedFish(): number {
      const fromEstimate = this.fishEstimate?.sessionUsedFish;
      if (typeof fromEstimate === "number" && Number.isFinite(fromEstimate)) {
        return Math.max(0, fromEstimate);
      }
      if (typeof this.sessionUsedFish === "number" && Number.isFinite(this.sessionUsedFish)) {
        return Math.max(0, this.sessionUsedFish);
      }
      return 0;
    },
    isSessionContinuation(): boolean {
      return this.effectiveSessionUsedFish > 0;
    },
    currentFish(): number {
      return this.$store.getters["fishModule/currentFish"] as number;
    },
    fishShortage(): number {
      return Math.max(0, this.expectedCharge.chargeFish - this.currentFish);
    },
    isFishInsufficient(): boolean {
      return (
        !!this.fishEstimate?.withinLimit
        && this.expectedCharge.chargeFish > 0
        && this.fishShortage > 0
      );
    },
    isFishEstimateUnavailable(): boolean {
      return (
        this.fishEstimate?.withinLimit === true
        && this.expectedCharge.requiredFish == null
      );
    },
    canProceed(): boolean {
      if (this.selectedRuleIds.length === 0) return false;
      if (this.isFishEstimateLoading) return false;
      if (this.fishEstimateError) return false;
      if (this.fishEstimate && !this.fishEstimate.withinLimit) return false;
      if (this.isFishEstimateUnavailable) return false;
      if (this.isFishInsufficient) return false;
      return true;
    },
    proceedBlockReason(): string | null {
      if (this.selectedRuleIds.length === 0) return null;
      if (this.isFishEstimateLoading) return null;
      if (this.fishEstimateError) {
        return this.$t("proofreadingItemSelectDialog.estimateError") as string;
      }
      if (this.fishEstimate && !this.fishEstimate.withinLimit) {
        return this.$t("proofreadingItemSelectDialog.charsExceeded", {
          max: PROOFREADING_FISH_MAX_CHARS.toLocaleString(this.$i18n.locale || "ja"),
        }) as string;
      }
      if (this.isFishEstimateUnavailable) {
        return this.$t("proofreadingItemSelectDialog.estimateUnavailable") as string;
      }
      if (this.isFishInsufficient) {
        return this.$t("proofreadingReviseManuscriptPage.fishInsufficient") as string;
      }
      return null;
    },
    isFreePlan(): boolean {
      const plan = this.$store.getters["billingModule/plan"] as Plan | undefined;
      return (plan ?? Plan.free) === Plan.free;
    },
  },
  mounted() {
    if (this.novelId) {
      this.proofreadingBasicStore.setActiveNovelId(this.novelId);
    }
    document.addEventListener("keydown", this.onKeydown);
    window.addEventListener("popstate", this.onPopState);
    this.$nextTick(() => {
      const form = this.$refs.dialogForm as HTMLFormElement | undefined;
      form?.focus();
    });
    this.loadFishEstimate();
    this.ensureProperNounsLoaded();
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onKeydown);
    window.removeEventListener("popstate", this.onPopState);
  },
  methods: {
    async ensureProperNounsLoaded() {
      if (!this.novelId) return;
      this.isProperNounLoading = true;
      try {
        await this.$store.dispatch("properNounModule/initialize", this.novelId);
      } catch {
        // 固有名詞一覧が取れなくても校正自体は続行できる
      } finally {
        this.isProperNounLoading = false;
      }
    },
    onToggleExcludeProperNouns() {
      if (this.isExecuting) return;
      this.proofreadingBasicStore.setExcludeProperNounsEnabled(!this.excludeProperNounsEnabled);
    },
    async onClickProperNounSettings() {
      if (this.isExecuting || !this.excludeProperNounsEnabled || this.isProperNounExcludeDialogOpen) {
        return;
      }
      this.isProperNounExcludeDialogOpen = true;
      try {
        await this.ensureProperNounsLoaded();
        const dialog = new Dialog(ProofreadingProperNounExcludeDialog);
        const options: ProofreadingProperNounExcludeDialogOptions = {
          properNouns: this.properNouns,
          initialSelectedIds: this.excludedProperNounIds,
          isLoading: this.isProperNounLoading,
        };
        const result = (await dialog.show(options)) as
          | ProofreadingProperNounExcludeDialogResult
          | undefined;

        if (result?.confirmed) {
          const allIds = this.properNouns.map((p) => p.properNounId);
          const selected = result.selectedIds;
          // 0 件選択は「除外なし」ではなく「全件」に正規化する（トグル ON との矛盾防止）
          if (selected.length === 0) {
            this.proofreadingBasicStore.setExcludedProperNounIds(null);
            return;
          }
          const isAll =
            allIds.length === selected.length
            && allIds.every((id) => selected.includes(id));
          this.proofreadingBasicStore.setExcludedProperNounIds(isAll ? null : selected);
          return;
        }

        if (result && "openManager" in result && result.openManager) {
          this.isProperNounModalOpen = true;
        }
      } finally {
        this.isProperNounExcludeDialogOpen = false;
      }
    },
    async onProperNounModalClose() {
      this.isProperNounModalOpen = false;
      await this.ensureProperNounsLoaded();
      // 管理画面から戻ったら個別選択ダイアログを再開
      if (this.excludeProperNounsEnabled) {
        await this.onClickProperNounSettings();
      }
    },
    async loadFishEstimate() {
      if (!this.novelId || this.manuscripts.length === 0) {
        this.fishEstimate = null;
        this.fishEstimateError = false;
        return;
      }
      this.isFishEstimateLoading = true;
      this.fishEstimateError = false;
      try {
        await this.$store.dispatch("fishModule/fetchFishData");
        this.fishEstimate = await estimateProofreadingFish({
          novelId: this.novelId,
          manuscripts: this.manuscripts,
        });
      } catch {
        this.fishEstimate = null;
        this.fishEstimateError = true;
      } finally {
        this.isFishEstimateLoading = false;
      }
    },
    onPopState() {
      // ブラウザ戻る/進む時は実行中でもオーバーレイを残さない
      this.$close({ confirmed: false, selectedRuleIds: [] });
    },
    checkboxFill(checked: boolean): string {
      return checked ? this.checkedFill : this.uncheckedFill;
    },
    optionRowClass(optionIndex: number): Record<string, boolean> {
      return {
        "row--option-surface-a": optionIndex % 2 === 0,
        "row--option-surface-b": optionIndex % 2 === 1,
      };
    },
    isPremiumCategory(category: GroupedCategoryView): boolean {
      return this.isFreePlan && !isFreeProofreadingCategory(category.id);
    },
    isCategoryChecked(category: GroupedCategoryView): boolean {
      return category.options.every((option) => this.selectedRuleIdSet.has(option.id));
    },
    isOptionChecked(optionId: string): boolean {
      return this.selectedRuleIdSet.has(optionId);
    },
    commitSelection(nextIds: Set<string>) {
      const ordered = this.rules
        .map((r: ProofreadingRuleEntry) => r.rule)
        .filter((id: string) => nextIds.has(id));
      this.proofreadingBasicStore.setSelectedRuleIds(ordered);
    },
    onToggleCategory(category: GroupedCategoryView) {
      if (this.isExecuting) return;
      if (this.isPremiumCategory(category)) return;
      const next = new Set(this.selectedRuleIdSet);
      const turningOn = !this.isCategoryChecked(category);
      category.options.forEach((option) => {
        if (turningOn) {
          next.add(option.id);
        } else {
          next.delete(option.id);
        }
      });
      this.commitSelection(next);
    },
    onToggleOption(category: GroupedCategoryView, option: GroupedRuleOption) {
      if (this.isExecuting) return;
      if (this.isPremiumCategory(category)) return;

      const next = new Set(this.selectedRuleIdSet);
      if (next.has(option.id)) {
        next.delete(option.id);
      } else {
        next.add(option.id);
      }
      this.commitSelection(next);
    },
    async onClickRuleInfo(option: GroupedRuleOption) {
      if (this.isExecuting || this.isDescriptionDialogOpen) return;
      this.isDescriptionDialogOpen = true;
      try {
        const dialog = new Dialog(ProofreadingRuleDescriptionDialog);
        await dialog.show({
          label: option.label,
          description: option.description,
        });
      } finally {
        this.isDescriptionDialogOpen = false;
      }
    },
    onKeydown(e: KeyboardEvent) {
      if (
        this.isExecuting
        || this.isDescriptionDialogOpen
        || this.isProperNounExcludeDialogOpen
        || this.isProperNounModalOpen
        || this.isFishConfirmDialogOpen
      ) {
        return;
      }
      if (e.key === "Escape") {
        this.onClickClose();
        return;
      }
      if (e.key !== "Enter" || e.isComposing) return;
      if (e.target instanceof HTMLButtonElement) return;
      if (!this.canProceed) return;

      e.preventDefault();
      this.onClickNext();
    },
    async onClickNext() {
      if (!this.canProceed || this.isExecuting) return;

      const selectedRuleIds = [...this.selectedRuleIds];

      // セッション内で課金が発生する初回（および差額課金）だけ確認ダイアログを出す
      const { chargeFish, isFree } = this.expectedCharge;
      if (!isFree && chargeFish > 0) {
        this.isFishConfirmDialogOpen = true;
        try {
          const confirmDialog = new Dialog(ProofreadingFishConfirmDialog);
          const options: ProofreadingFishConfirmDialogOptions = {
            chargeFish,
            currentFish: this.currentFish,
            isSessionContinuation: this.isSessionContinuation,
          };
          const confirmed = (await confirmDialog.show(options)) as
            | ProofreadingFishConfirmDialogResult
            | undefined;
          if (!confirmed) {
            return;
          }
        } finally {
          this.isFishConfirmDialogOpen = false;
        }
      }

      if (!this.onConfirm) {
        this.$close({
          confirmed: true,
          selectedRuleIds,
        });
        return;
      }

      this.isExecuting = true;
      try {
        await this.onConfirm(selectedRuleIds);
        this.$close({
          confirmed: true,
          selectedRuleIds,
        });
      } catch {
        // 呼び出し元でエラー通知済み。ダイアログは開いたまま再試行可能にする。
      } finally {
        this.isExecuting = false;
      }
    },
    onClickClose() {
      if (
        this.isExecuting
        || this.isDescriptionDialogOpen
        || this.isProperNounExcludeDialogOpen
        || this.isProperNounModalOpen
        || this.isFishConfirmDialogOpen
      ) {
        return;
      }
      this.$close({ confirmed: false, selectedRuleIds: [] });
    },
    onClickOutside() {
      if (
        this.isExecuting
        || this.isDescriptionDialogOpen
        || this.isProperNounExcludeDialogOpen
        || this.isProperNounModalOpen
        || this.isFishConfirmDialogOpen
      ) {
        return;
      }
      this.$close({ confirmed: false, selectedRuleIds: [] });
    },
  },
});

export type ProofreadingItemSelectDialogResult = {
  confirmed: boolean;
  selectedRuleIds: string[];
};

export type ProofreadingItemSelectDialogOptions = {
  onConfirm?: (selectedRuleIds: string[]) => Promise<void>;
  novelId?: string;
  manuscripts?: PostRuleBasedManuscriptParam[];
  sessionUsedFish?: number | null;
};
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.mask {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9000;
  background-color: var(--color-overlay);
}

.dialog {
  display: flex;
  flex-direction: column;
  width: min(640px, 92vw);
  height: min(calc(100vh - 96px), 1200px);
  margin: 0;
  border: none;
  background-color: var(--color-surface-subtle);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  color: var(--color-text-normal);
  overflow: hidden;

  &:focus {
    outline: none;
  }
}

.dialog__header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 48px;
  border-bottom: solid 1px var(--color-border);
}

.dialog__title {
  margin: 0;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
}

.dialog__close {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border: none;
  border-radius: var(--radius-rounded);
  background: transparent;
  color: var(--color-text-normal);
  font-size: 20px;
  line-height: 1;
  cursor: pointer;

  @include hover-overlay;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.dialog__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 0px 16px 20px;

  &--disabled {
    pointer-events: none;
    opacity: 0.6;
  }
}

.proper-noun-exclude {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  margin: 8px 0 12px;
}

.proper-noun-exclude__row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.proper-noun-exclude__toggle {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  color: var(--color-text-normal);

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.proper-noun-exclude__label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
}

.proper-noun-exclude__settings {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: var(--radius-rounded);
  background: transparent;
  color: var(--color-text-low);
  cursor: pointer;

  @include hover-overlay;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  svg {
    width: 20px;
    height: 20px;
  }
}

.switch {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  width: 44px;
  height: 26px;
  border-radius: 999px;
  background-color: var(--color-inactive, #cfcfcf);
  transition: background-color 0.15s ease;

  &[data-on="true"] {
    background-color: var(--color-primary);
  }
}

.switch__knob {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  transition: transform 0.15s ease;

  .switch[data-on="true"] & {
    transform: translateX(18px);
  }
}

.dialog__footer {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  padding: 12px 20px;
  border-top: solid 1px var(--color-border);
}

.dialog__blocker {
  margin: 0;
  text-align: center;
  font-size: var(--font-size-sm);
  line-height: 1.5;
  color: var(--color-danger, #c62828);
}

.retry-button {
  border: none;
  border-radius: var(--radius-rounded);
  padding: 10px 24px;
  background-color: var(--color-surface-base, #ebebeb);
  color: var(--color-text-normal);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.next-button {
  border: none;
  border-radius: var(--radius-rounded);
  padding: 12px 48px;
  background-color: var(--color-primary);
  color: var(--color-text-invert);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  align-self: flex-end;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.list {
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 24px;
  padding: 12px 16px;
  border-radius: var(--radius-sm);
  cursor: pointer;
  user-select: none;
}

.row--option-surface-a {
  background-color: var(--color-background-normal);
}

.row--option-surface-b {
  background-color: var(--color-surface-base);
}

.category-checkbox {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  line-height: 0;
  cursor: pointer;

  &[data-disabled="true"] {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.category-header-label-row {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 8px;
}

.category-header-label {
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.label {
  flex: 1;
  min-width: 0;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
}

.info-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: var(--radius-rounded);
  background: transparent;
  color: var(--color-text-low);
  font-size: var(--font-size-md);
  cursor: pointer;

  @include hover-overlay;
}

@include mq("sp") {
  .dialog {
    width: 100vw;
    height: 100vh;
    max-height: 100vh;
    border-radius: 0;
  }
}

::v-deep .proper-noun-modal--over-proofreading {
  z-index: 9200;
}
</style>
